import { useMemo, useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import HTMLFlipBook from "react-pageflip-enhanced";
import { Product } from "@/mocks/products";
import { getCatalogUrl } from "@/lib/slug";
import DOMPurify from "dompurify";
import {
  BuilderPage,
  BuilderBlock,
  HeroBlockData,
  StoryBlockData,
  ApplicationsBlockData,
  CertificationsBlockData,
  CTABlockData,
  SpecsGridBlockData,
  GalleryBlockData,
} from "@/pages/admin/components/builder/types";

interface Props {
  product: Product;
}

/* ── Sanitize helper ─────────────────────────────────────────────────────── */

const sanitize = (html: string): string =>
  DOMPurify.sanitize(html, {
    ADD_ATTR: ["target", "rel", "style"],
    ALLOWED_ATTR: [
      "href", "src", "alt", "title", "target", "rel",
      "style", "class", "width", "height",
    ],
  });

/* ── Extract every image from builder pages into individual flipbook pages ─ */

interface ImagePage {
  imageUrl: string;
  title: string;
  caption: string;
  type: string;
}

const extractImagePagesFromBuilderPages = (pages: BuilderPage[]): ImagePage[] => {
  const imagePages: ImagePage[] = [];

  pages.forEach((page) => {
    page.blocks.forEach((block) => {
      switch (block.data.type) {
        case "hero": {
          const d = block.data as HeroBlockData;
          if (d.backgroundImage) {
            imagePages.push({
              imageUrl: d.backgroundImage,
              title: d.title || page.name,
              caption: d.subtitle || "",
              type: "hero",
            });
          }
          break;
        }
        case "story": {
          const d = block.data as StoryBlockData;
          d.items.forEach((item) => {
            if (item.image) {
              imagePages.push({
                imageUrl: item.image,
                title: item.title,
                caption: item.accent || "",
                type: "story",
              });
            }
          });
          break;
        }
        case "applications": {
          const d = block.data as ApplicationsBlockData;
          d.items.forEach((item) => {
            if (item.image_url) {
              imagePages.push({
                imageUrl: item.image_url,
                title: item.title,
                caption: "",
                type: "applications",
              });
            }
          });
          break;
        }
        case "certifications": {
          const d = block.data as CertificationsBlockData;
          d.items.forEach((item) => {
            if (item.image_url) {
              imagePages.push({
                imageUrl: item.image_url,
                title: item.title,
                caption: item.type,
                type: "certifications",
              });
            }
          });
          break;
        }
        case "gallery": {
          const d = block.data as GalleryBlockData;
          d.images.forEach((img) => {
            if (img.url) {
              imagePages.push({
                imageUrl: img.url,
                title: img.caption || d.title || page.name,
                caption: "",
                type: "gallery",
              });
            }
          });
          break;
        }
        default:
          break;
      }
    });
  });

  return imagePages;
};

/* ── Compact block renderer for flipbook pages (~520x734) ──────────────── */

const renderBlockCompact = (block: BuilderBlock): React.ReactNode => {
  switch (block.data.type) {
    case "hero": {
      const d = block.data as HeroBlockData;
      return (
        <div className="relative w-full h-40 rounded-lg overflow-hidden mb-2">
          {d.backgroundImage ? (
            <img
              src={d.backgroundImage}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-[#004A99] to-[#00AEEF]" />
          )}
          <div
            className="absolute inset-0"
            style={{ background: `rgba(0,0,0,${d.overlayOpacity / 100})` }}
          />
          <div className="absolute inset-0 flex flex-col justify-end p-3">
            {d.tagline && (
              <span className="text-[8px] font-bold tracking-widest text-white/70 uppercase">
                {d.tagline}
              </span>
            )}
            {d.title && (
              <h3 className="text-sm font-bold text-white leading-tight">
                {d.title}
              </h3>
            )}
            {d.subtitle && (
              <p className="text-[10px] text-white/70 mt-0.5 line-clamp-2">
                {d.subtitle}
              </p>
            )}
            {d.ctaLabel && (
              <span className="inline-block mt-1 px-2.5 py-0.5 bg-white text-[#004A99] text-[9px] font-bold rounded-full w-fit">
                {d.ctaLabel}
              </span>
            )}
          </div>
        </div>
      );
    }
    case "story": {
      const d = block.data as StoryBlockData;
      return (
        <div className="space-y-2">
          {d.items.map((item) => (
            <div key={item.id}>
              {item.accent && (
                <span className="text-[8px] font-bold tracking-wider text-[#004A99] uppercase bg-[#EDF2FB] px-2 py-0.5 rounded-full inline-block">
                  {item.accent}
                </span>
              )}
              {item.title && (
                <h4 className="text-xs font-bold text-gray-900 mt-0.5 leading-snug">
                  {item.title}
                </h4>
              )}
              {item.image && (
                <img
                  src={item.image}
                  alt=""
                  className="w-full h-20 object-cover rounded-lg mt-1"
                />
              )}
              {item.content && (
                <div
                  className="text-[10px] text-gray-600 leading-relaxed line-clamp-5 mt-0.5
                    [&_p]:mb-1 [&_ul]:list-disc [&_ul]:pl-3 [&_ul>li]:mb-0.5
                    [&_strong]:font-bold [&_strong]:text-gray-900
                    [&_h3]:font-bold [&_h3]:text-gray-800 [&_h3]:text-[10px]"
                  dangerouslySetInnerHTML={{
                    __html: sanitize(item.content),
                  }}
                />
              )}
            </div>
          ))}
        </div>
      );
    }
    case "applications": {
      const d = block.data as ApplicationsBlockData;
      if (!d.items || d.items.length === 0) return null;
      return (
        <div>
          <h4 className="text-[10px] font-bold text-gray-900 mb-1.5">
            {d.title || "Ứng dụng"}
          </h4>
          <div className="grid grid-cols-2 gap-1.5">
            {d.items.slice(0, 4).map((item) => (
              <div
                key={item.id}
                className="rounded-lg overflow-hidden border border-gray-100"
              >
                {item.image_url && (
                  <img
                    src={item.image_url}
                    alt=""
                    className="w-full h-14 object-cover"
                  />
                )}
                <div className="p-1">
                  <p className="text-[9px] font-bold text-gray-800 truncate">
                    {item.title}
                  </p>
                  {item.description && (
                    <p className="text-[8px] text-gray-400 line-clamp-2">
                      {item.description.replace(/<[^>]*>/g, "")}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    case "certifications": {
      const d = block.data as CertificationsBlockData;
      if (!d.items || d.items.length === 0) return null;
      const typeLabel: Record<string, string> = {
        certification: "Chứng nhận",
        patent: "Bằng sáng chế",
        award: "Giải thưởng",
      };
      return (
        <div>
          <h4 className="text-[10px] font-bold text-gray-900 mb-1.5">
            {d.title || "Chứng nhận"}
          </h4>
          <div className="grid grid-cols-2 gap-1.5">
            {d.items.slice(0, 4).map((item) => (
              <div
                key={item.id}
                className="flex flex-col items-center text-center p-1.5 rounded-lg border border-gray-100"
              >
                {item.image_url && (
                  <img
                    src={item.image_url}
                    alt=""
                    className="w-8 h-8 object-contain mb-0.5"
                  />
                )}
                <span className="text-[7px] font-bold text-[#004A99] bg-[#EDF2FB] px-1.5 py-0.5 rounded-full">
                  {typeLabel[item.type] ?? item.type}
                </span>
                <p className="text-[9px] font-bold text-gray-800 leading-snug mt-0.5">
                  {item.title}
                </p>
              </div>
            ))}
          </div>
        </div>
      );
    }
    case "cta": {
      const d = block.data as CTABlockData;
      return (
        <div
          className="p-3 rounded-lg text-center"
          style={{ background: d.backgroundColor, color: d.textColor }}
        >
          {d.title && (
            <h4 className="text-xs font-bold">{d.title}</h4>
          )}
          {d.subtitle && (
            <p className="text-[9px] opacity-80 mt-0.5">{d.subtitle}</p>
          )}
          {d.buttonLabel && (
            <span
              className="inline-block mt-1.5 px-3 py-1 rounded-full text-[9px] font-bold"
              style={{ background: d.buttonColor, color: "#fff" }}
            >
              {d.buttonLabel}
            </span>
          )}
          {d.showForm && (
            <div className="mt-2 grid grid-cols-2 gap-1">
              {d.formFields.slice(0, 4).map((f, i) => (
                <div
                  key={i}
                  className={`bg-white/20 rounded px-2 py-1 text-[8px] text-left ${f.type === "textarea" ? "col-span-2" : ""}`}
                >
                  <span className="opacity-60">{f.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }
    case "specs_grid": {
      const d = block.data as SpecsGridBlockData;
      if (!d.items || d.items.length === 0) return null;
      return (
        <div>
          <h4 className="text-[10px] font-bold text-gray-900 mb-1.5">
            {d.title || "Thông số"}
          </h4>
          <div className="grid grid-cols-2 gap-1">
            {d.items.slice(0, 8).map((item, i) => (
              <div key={i} className="bg-gray-50 rounded-lg p-1.5">
                <p className="text-[7px] font-bold text-gray-400 uppercase tracking-wider">
                  {item.label}
                </p>
                <p className="text-[9px] font-bold text-gray-900">
                  {item.value}
                </p>
              </div>
            ))}
          </div>
        </div>
      );
    }
    case "gallery": {
      const d = block.data as GalleryBlockData;
      if (!d.images || d.images.length === 0) return null;
      return (
        <div>
          <h4 className="text-[10px] font-bold text-gray-900 mb-1.5">
            {d.title || "Gallery"}
          </h4>
          <div className="grid grid-cols-2 gap-1">
            {d.images.slice(0, 6).map((img, i) => (
              <img
                key={i}
                src={img.url}
                alt={img.caption}
                className="w-full h-14 object-cover rounded-lg"
              />
            ))}
          </div>
        </div>
      );
    }
    default:
      return null;
  }
};

/* ── Render a single BuilderPage as a flipbook page (fallback, no scroll) ─── */

const renderBuilderPage = (page: BuilderPage): React.ReactNode => {
  return (
    <div className="h-full w-full bg-[#FDFCF8] p-4 flex flex-col gap-1.5 overflow-hidden">
      {/* Page header */}
      <div className="flex items-center gap-1.5 mb-0.5 shrink-0">
        <div className="w-4 h-4 flex items-center justify-center bg-[#004A99] rounded">
          <i className="ri-file-text-line text-white text-[7px]" />
        </div>
        <h3 className="text-[10px] font-bold text-gray-900 line-clamp-1">{page.name}</h3>
      </div>

      {/* Blocks content — NO scroll, overflow hidden */}
      <div className="flex-1 overflow-hidden space-y-1.5 min-h-0">
        {page.blocks.slice(0, 4).map((block) => (
          <div key={block.id}>{renderBlockCompact(block)}</div>
        ))}
      </div>

      {/* Page footer */}
      <div className="text-[6px] text-gray-300 text-center pt-0.5 border-t border-gray-100 shrink-0">
        {page.name}
      </div>
    </div>
  );
};

/* ── Render a single image as a full flipbook page (fit-to-page) ─────────── */

const renderImagePage = (imgPage: ImagePage): React.ReactNode => {
  // Flipbook direct images — show full image without cropping
  if (imgPage.type === "flipbook") {
    return (
      <div className="h-full w-full flex items-center justify-center bg-white" style={{ overflow: "hidden" }}>
        <img
          src={imgPage.imageUrl}
          alt=""
          className="w-full h-full object-contain"
          style={{ display: "block" }}
        />
      </div>
    );
  }
  return (
    <div className="h-full w-full bg-[#FDFCF8] flex flex-col" style={{ overflow: "hidden" }}>
      {/* Image area — take maximum space, fit without cropping */}
      <div className="flex-1 flex items-center justify-center p-2" style={{ minHeight: 0 }}>
        <img
          src={imgPage.imageUrl}
          alt={imgPage.title}
          className="max-w-full max-h-full object-contain rounded-md"
          style={{ display: "block" }}
        />
      </div>
      {/* Caption area */}
      {(imgPage.title || imgPage.caption) && (
        <div className="px-3 pb-2 text-center" style={{ flexShrink: 0 }}>
          {imgPage.title && (
            <p className="text-xs font-bold text-gray-900 line-clamp-1">{imgPage.title}</p>
          )}
          {imgPage.caption && (
            <p className="text-[10px] text-gray-500 mt-0.5 capitalize line-clamp-1">{imgPage.caption}</p>
          )}
        </div>
      )}
      {/* Page footer */}
      <div className="text-[6px] text-gray-300 text-center py-1 border-t border-gray-100" style={{ flexShrink: 0 }}>
        {imgPage.type === "hero" ? "Trang bìa" : imgPage.type === "gallery" ? "Thư viện ảnh" : "Nội dung"}
      </div>
    </div>
  );
};

/* ── Cover & Back Cover helpers ─────────────────────────────────────────── */

function generateCoverPage(product: Product): React.ReactNode {
  const raw = product as unknown as Record<string, unknown>;
  const mediaItems = Array.isArray(raw.media_items)
    ? (raw.media_items as Array<{ url: string; type: string; is_cover?: boolean }>)
    : [];
  const images = mediaItems
    .filter((m) => m.type === "image" || !m.type)
    .map((m) => m.url);
  if (images.length === 0) images.push(...product.images);

  const coverImage =
    mediaItems.find((m) => m.is_cover && m.type === "image")?.url ??
    images[0] ??
    product.images[0] ??
    "";

  return (
    <div className="h-full w-full relative overflow-hidden bg-[#1a1a2e]">
      {coverImage && (
        <img
          src={coverImage}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-black/10" />
      <div className="absolute left-0 right-0 bottom-3 px-4 pb-2 flex flex-col gap-1">
        <span className="text-[9px] font-bold bg-white/20 text-white px-2.5 py-0.5 rounded-full uppercase tracking-wider w-fit max-w-full truncate">
          {product.categoryName}
        </span>
        <h1 className="text-[16px] font-bold text-white leading-tight line-clamp-2">
          {product.name}
        </h1>
        {product.nameKr && (
          <p className="text-white/60 text-[10px] truncate">{product.nameKr}</p>
        )}
        <p className="text-white/70 text-[10px] font-medium truncate">
          {product.companyName}
        </p>
      </div>
    </div>
  );
}

function generateBackCoverPage(product: Product): React.ReactNode {
  return (
    <div className="h-full w-full bg-[#1a1a2e] text-white p-5 flex flex-col justify-between">
      <div>
        <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg mb-3">
          <i className="ri-book-open-line text-white text-lg" />
        </div>
        <h3 className="text-base font-bold text-white mb-0.5 line-clamp-2">{product.name}</h3>
        <p className="text-white/60 text-xs truncate">{product.companyName}</p>
      </div>
      <div className="space-y-1.5">
        <p className="text-[10px] text-white/40">
          Hiệp hội công nghiệp Carbon Nano Hàn Quốc
        </p>
        <p className="text-[10px] text-white/40">Nền tảng B2B kết nối doanh nghiệp Hàn - Việt</p>
      </div>
      <div className="text-center pt-3 border-t border-white/10">
        <p className="text-[9px] text-white/30">
          Catalog B2B · Chỉ dùng cho mục đích tư vấn kinh doanh
        </p>
      </div>
    </div>
  );
}

/* ── Auto-generate fallback (legacy, when no builder data) ──────────────── */

function generateAutoFlipbookPages(product: Product): React.ReactNode[] {
  const raw = product as unknown as Record<string, unknown>;
  const pages: React.ReactNode[] = [];

  // Gather images
  const mediaItems = Array.isArray(raw.media_items)
    ? (raw.media_items as Array<{ url: string; type: string; is_cover?: boolean }>)
    : [];
  const images = mediaItems
    .filter((m) => m.type === "image" || !m.type)
    .map((m) => m.url);
  if (images.length === 0) images.push(...product.images);

  const coverImage =
    mediaItems.find((m) => m.is_cover && m.type === "image")?.url ??
    images[0] ??
    product.images[0] ??
    "";

  // 1. Cover
  pages.push(
    <div className="h-full w-full relative overflow-hidden bg-[#1a1a2e]">
      {coverImage && (
        <img
          src={coverImage}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-black/10" />
      <div className="absolute left-0 right-0 bottom-3 px-4 pb-2 flex flex-col gap-1">
        <span className="text-[9px] font-bold bg-white/20 text-white px-2.5 py-0.5 rounded-full uppercase tracking-wider w-fit max-w-full truncate">
          {product.categoryName}
        </span>
        <h1 className="text-[16px] font-bold text-white leading-tight line-clamp-2">
          {product.name}
        </h1>
        {product.nameKr && (
          <p className="text-white/60 text-[10px] truncate">{product.nameKr}</p>
        )}
        <p className="text-white/70 text-[10px] font-medium truncate">
          {product.companyName}
        </p>
      </div>
    </div>
  );

  // 2. Product Overview
  const overviewImage = images[1] ?? images[0] ?? "";
  pages.push(
    <div className="h-full w-full bg-[#FDFCF8] p-5 flex flex-col">
      <div className="mb-3">
        <span className="text-[9px] font-bold text-[#004A99] uppercase tracking-wider">
          Tổng quan
        </span>
        <h2 className="text-lg font-bold text-gray-900 mt-0.5 leading-tight line-clamp-2">{product.name}</h2>
      </div>
      {overviewImage && (
        <div className="w-full h-36 rounded-lg overflow-hidden mb-3 flex-shrink-0">
          <img
            src={overviewImage}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        </div>
      )}
      {product.description && (
        <p className="text-xs text-gray-700 leading-relaxed mb-3 line-clamp-4">
          {product.description}
        </p>
      )}
      <div className="mt-auto space-y-1.5">
        <div className="flex items-center gap-2 text-[10px]">
          <span className="text-gray-400 w-16 flex-shrink-0">Danh mục</span>
          <span className="text-gray-800 font-medium truncate">{product.categoryName}</span>
        </div>
        <div className="flex items-center gap-2 text-[10px]">
          <span className="text-gray-400 w-16 flex-shrink-0">Phân loại</span>
          <span className="text-gray-800 font-medium truncate">{product.subCategoryName}</span>
        </div>
        <div className="flex items-center gap-2 text-[10px]">
          <span className="text-gray-400 w-16 flex-shrink-0">Nhà SX</span>
          <span className="text-gray-800 font-medium truncate">{product.companyName}</span>
        </div>
        {product.price && product.price !== "Liên hệ để biết giá" && (
          <div className="flex items-center gap-2 text-[10px]">
            <span className="text-gray-400 w-16 flex-shrink-0">Giá</span>
            <span className="text-[#004A99] font-semibold truncate">{product.price}</span>
          </div>
        )}
      </div>
    </div>
  );

  // 3. Description Detail
  if (product.descriptionDetail) {
    const isHtml = product.descriptionDetail.includes("<");
    const cleanHtml = isHtml
      ? DOMPurify.sanitize(product.descriptionDetail, {
          ADD_ATTR: ["target", "rel", "style"],
          ALLOWED_ATTR: [
            "href", "src", "alt", "title", "target", "rel", "style", "class", "width", "height",
          ],
        })
      : "";

    pages.push(
      <div className="h-full w-full bg-[#FDFCF8] p-5 flex flex-col">
        <span className="text-[9px] font-bold text-[#004A99] uppercase tracking-wider mb-2">
          Chi tiết sản phẩm
        </span>
        <div className="flex-1 overflow-hidden min-h-0">
          {isHtml ? (
            <div
              className="text-[11px] text-gray-700 leading-relaxed
                [&_p]:mb-1.5 [&_ul]:list-disc [&_ul]:pl-3 [&_ul>li]:mb-0.5
                [&_ol]:list-decimal [&_ol]:pl-3 [&_ol>li]:mb-0.5
                [&_strong]:font-bold [&_strong]:text-gray-900
                [&_h3]:font-bold [&_h3]:text-gray-800 [&_h3]:mb-1 [&_h3]:text-xs
                [&_img]:rounded-lg [&_img]:my-1 [&_img]:max-w-full [&_img]:max-h-32"
              dangerouslySetInnerHTML={{ __html: cleanHtml }}
            />
          ) : (
            <p className="text-[11px] text-gray-700 leading-relaxed whitespace-pre-line line-clamp-[16]">
              {product.descriptionDetail}
            </p>
          )}
        </div>
      </div>
    );
  }

  // 4. Section pages
  const sections = product.sections ?? [];
  sections.forEach((section, idx) => {
    const secHtml = section.content
      ? DOMPurify.sanitize(section.content, {
          ADD_ATTR: ["target", "rel", "style"],
          ALLOWED_ATTR: [
            "href", "src", "alt", "title", "target", "rel", "style", "class", "width", "height",
          ],
        })
      : "";
    const secIsHtml = secHtml.includes("<");

    pages.push(
      <div className="h-full w-full bg-[#FDFCF8] p-5 flex flex-col">
        <span className="text-[9px] font-bold text-[#004A99] uppercase tracking-wider mb-1.5">
          Phần {idx + 1}
        </span>
        <h3 className="text-base font-bold text-gray-900 mb-2 leading-tight line-clamp-2">{section.title}</h3>
        {section.image && (
          <div className="w-full h-32 rounded-lg overflow-hidden mb-2 flex-shrink-0">
            <img
              src={section.image}
              alt={section.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <div className="flex-1 overflow-hidden min-h-0">
          {secIsHtml ? (
            <div
              className="text-[11px] text-gray-700 leading-relaxed
                [&_p]:mb-1.5 [&_ul]:list-disc [&_ul]:pl-3 [&_ul>li]:mb-0.5
                [&_ol]:list-decimal [&_ol]:pl-3 [&_ol>li]:mb-0.5
                [&_strong]:font-bold [&_strong]:text-gray-900"
              dangerouslySetInnerHTML={{ __html: secHtml }}
            />
          ) : (
            <p className="text-[11px] text-gray-700 leading-relaxed line-clamp-[14]">{section.content}</p>
          )}
        </div>
      </div>
    );
  });

  // 5. Applications
  const apps = raw.applications as
    | Array<{
        image_url?: string;
        image?: string;
        title?: string;
        description?: string;
      }>
    | undefined;
  if (apps && apps.length > 0) {
    pages.push(
      <div className="h-full w-full bg-[#FDFCF8] p-5 flex flex-col">
        <span className="text-[9px] font-bold text-[#004A99] uppercase tracking-wider mb-2">
          Ứng dụng thực tế
        </span>
        <h3 className="text-base font-bold text-gray-900 mb-3">Ứng Dụng</h3>
        <div className="flex-1 overflow-hidden min-h-0 space-y-2.5">
          {apps.map((app, i) => (
            <div key={i} className="flex gap-2.5 items-start">
              {(app.image_url || app.image) && (
                <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0">
                  <img
                    src={app.image_url ?? app.image}
                    alt={app.title ?? ""}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h4 className="text-xs font-bold text-gray-900 mb-0.5 line-clamp-2">{app.title}</h4>
                {app.description && (
                  <p className="text-[10px] text-gray-500 line-clamp-2">{app.description}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // 6. Patents / Certifications
  const patents = product.patents_certifications;
  if (patents && patents.length > 0) {
    const typeLabel: Record<string, { label: string; color: string; icon: string }> = {
      patent: { label: "Bằng Sáng Chế", color: "#004A99", icon: "ri-file-text-line" },
      certification: { label: "Chứng Nhận", color: "#059669", icon: "ri-medal-line" },
      award: { label: "Giải Thưởng", color: "#D97706", icon: "ri-trophy-line" },
    };

    pages.push(
      <div className="h-full w-full bg-[#FDFCF8] p-5 flex flex-col">
        <span className="text-[9px] font-bold text-[#004A99] uppercase tracking-wider mb-2">
          Tin tưởng &amp; chứng nhận
        </span>
        <h3 className="text-base font-bold text-gray-900 mb-3">
          Bằng Sáng Chế &amp; Chứng Nhận
        </h3>
        <div className="flex-1 overflow-hidden min-h-0 grid grid-cols-2 gap-2">
          {patents.slice(0, 4).map((item, i) => {
            const meta = typeLabel[item.type] ?? typeLabel.certification;
            return (
              <div
                key={i}
                className="flex flex-col items-center text-center p-2 rounded-lg border border-gray-100 bg-white"
              >
                {item.image_url ? (
                  <div className="w-full h-16 rounded-md overflow-hidden mb-1.5">
                    <img
                      src={item.image_url}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div
                    className="w-8 h-8 flex items-center justify-center rounded-lg mb-1.5"
                    style={{ background: `${meta.color}15` }}
                  >
                    <i className={`${meta.icon} text-base`} style={{ color: meta.color }} />
                  </div>
                )}
                <span
                  className="text-[8px] font-bold px-1.5 py-0.5 rounded-full mb-0.5"
                  style={{ background: `${meta.color}12`, color: meta.color }}
                >
                  {meta.label}
                </span>
                <h4 className="text-[10px] font-bold text-gray-800 leading-tight line-clamp-2">
                  {item.title}
                </h4>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // 7. Specs
  const supabaseSpecs = raw.specs as
    | Array<{ label: string; value: string }>
    | undefined;
  const rows: Array<{ label: string; value: string }> = [
    { label: "Danh mục", value: product.categoryName },
    { label: "Phân loại", value: product.subCategoryName },
    { label: "Thương hiệu / Nhà sản xuất", value: product.companyName },
    ...(product.registeredDate
      ? [{ label: "Ngày đăng ký", value: product.registeredDate }]
      : []),
    ...(Array.isArray(supabaseSpecs) ? supabaseSpecs : []),
  ].filter((r) => r.value && r.value.trim() !== "");

  if (rows.length > 0) {
    pages.push(
      <div className="h-full w-full bg-[#FDFCF8] p-5 flex flex-col">
        <span className="text-[9px] font-bold text-[#004A99] uppercase tracking-wider mb-2">
          Thông số kỹ thuật
        </span>
        <h3 className="text-base font-bold text-gray-900 mb-3">Chi tiết đầy đủ</h3>
        <div className="flex-1 overflow-hidden min-h-0">
          <div className="space-y-0">
            {rows.slice(0, 12).map((row, i) => (
              <div
                key={i}
                className={`flex items-start py-2 px-2.5 rounded-md ${i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}
              >
                <span className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide w-1/3 flex-shrink-0 leading-tight">
                  {row.label}
                </span>
                <span className="text-[10px] font-medium text-gray-900 flex-1 leading-tight break-words">
                  {row.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // 8. Tags
  if (product.tags && product.tags.length > 0) {
    pages.push(
      <div className="h-full w-full bg-[#FDFCF8] p-5 flex flex-col">
        <span className="text-[9px] font-bold text-[#004A99] uppercase tracking-wider mb-2">
          Từ khóa
        </span>
        <h3 className="text-base font-bold text-gray-900 mb-3">Tags liên quan</h3>
        <div className="flex flex-wrap gap-1.5">
          {product.tags.map((tag) => (
            <span
              key={tag}
              className="text-[10px] bg-[#EEF4FF] text-[#004A99] px-2.5 py-1 rounded-full font-medium border border-[#004A99]/15"
            >
              #{tag}
            </span>
          ))}
        </div>
      </div>
    );
  }

  // 9. Contact
  if (product.managerPhone || product.managerEmail) {
    pages.push(
      <div className="h-full w-full bg-[#FDFCF8] p-5 flex flex-col">
        <span className="text-[9px] font-bold text-[#004A99] uppercase tracking-wider mb-2">
          Liên hệ
        </span>
        <h3 className="text-base font-bold text-gray-900 mb-3">Thông tin liên hệ</h3>
        <div className="space-y-2.5">
          {product.managerPhone && (
            <div className="flex items-center gap-2.5 p-2.5 bg-white rounded-lg border border-gray-100">
              <div className="w-7 h-7 flex items-center justify-center bg-[#EEF4FF] rounded-md flex-shrink-0">
                <i className="ri-phone-line text-[#004A99] text-xs" />
              </div>
              <div className="min-w-0">
                <p className="text-[9px] text-gray-400">Điện thoại</p>
                <p className="text-xs font-medium text-gray-900 truncate">
                  {product.managerPhone}
                </p>
              </div>
            </div>
          )}
          {product.managerEmail && (
            <div className="flex items-center gap-2.5 p-2.5 bg-white rounded-lg border border-gray-100">
              <div className="w-7 h-7 flex items-center justify-center bg-[#EEF4FF] rounded-md flex-shrink-0">
                <i className="ri-mail-line text-[#004A99] text-xs" />
              </div>
              <div className="min-w-0">
                <p className="text-[9px] text-gray-400">Email</p>
                <p className="text-xs font-medium text-gray-900 truncate">
                  {product.managerEmail}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // 10. Back Cover
  pages.push(
    <div className="h-full w-full bg-[#1a1a2e] text-white p-5 flex flex-col justify-between">
      <div>
        <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg mb-3">
          <i className="ri-book-open-line text-white text-lg" />
        </div>
        <h3 className="text-base font-bold text-white mb-0.5 line-clamp-2">{product.name}</h3>
        <p className="text-white/60 text-xs truncate">{product.companyName}</p>
      </div>
      <div className="space-y-1.5">
        <p className="text-[10px] text-white/40">
          Hiệp hội công nghiệp Carbon Nano Hàn Quốc
        </p>
        <p className="text-[10px] text-white/40">Nền tảng B2B kết nối doanh nghiệp Hàn - Việt</p>
      </div>
      <div className="text-center pt-3 border-t border-white/10">
        <p className="text-[9px] text-white/30">
          Catalog B2B · Chỉ dùng cho mục đích tư vấn kinh doanh
        </p>
      </div>
    </div>
  );

  return pages;
}

/* ── Main Component ─────────────────────────────────────────────────────── */

export default function FlipbookViewer({ product }: Props) {
  const [currentPage, setCurrentPage] = useState(0);
  const [copied, setCopied] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const flipbookRef = useRef<any>(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleFlipPrev = () => {
    try {
      flipbookRef.current?.pageFlip()?.flipPrev();
    } catch { /* ignore */ }
  };

  const handleFlipNext = () => {
    try {
      flipbookRef.current?.pageFlip()?.flipNext();
    } catch { /* ignore */ }
  };

  const pages = useMemo(() => {
    const raw = product as unknown as Record<string, unknown>;

    // 0. Try flipbook_images first — each image = 1 page
    const flipbookImages = (raw.flipbook_images ?? []) as string[];
    if (flipbookImages.length > 0) {
      const result: React.ReactNode[] = [];
      // Front cover
      result.push(generateCoverPage(product));
      // Each image as its own page
      flipbookImages.forEach((imgUrl) => {
        result.push(renderImagePage({ imageUrl: imgUrl, title: "", caption: "", type: "flipbook" }));
      });
      // Back cover
      result.push(generateBackCoverPage(product));
      return result;
    }

    // 1. Try catalog_builder_pages next
    const catalogPages = (raw.catalog_builder_pages ?? []) as BuilderPage[];
    const hasCatalogPages =
      catalogPages.length > 0 && catalogPages.some((p) => p.blocks.length > 0);

    // 2. Fallback to builder_pages
    const builderPages = (raw.builder_pages ?? []) as BuilderPage[];
    const hasBuilderPages =
      builderPages.length > 0 && builderPages.some((p) => p.blocks.length > 0);

    const sourcePages = hasCatalogPages
      ? catalogPages
      : hasBuilderPages
        ? builderPages
        : null;

    // 3. If builder pages exist, auto-split images into individual pages
    if (sourcePages) {
      const result: React.ReactNode[] = [];
      // Front cover
      result.push(generateCoverPage(product));

      // Extract all images from all blocks across all pages
      const imagePages = extractImagePagesFromBuilderPages(sourcePages);

      if (imagePages.length > 0) {
        // Each image becomes its own flipbook page — fit to page, no scroll
        imagePages.forEach((imgPage) => {
          result.push(renderImagePage(imgPage));
        });
      } else {
        // Fallback: render each builder page as a compact page (no scroll)
        sourcePages.forEach((page) => {
          if (page.blocks.length > 0) {
            result.push(renderBuilderPage(page));
          }
        });
      }

      // Back cover
      result.push(generateBackCoverPage(product));
      return result;
    }

    // 4. Fallback: auto-generate from product data
    return generateAutoFlipbookPages(product);
  }, [product]);

  const handleCopyLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    });
  };

  // Responsive flipbook dimensions
  // Desktop: ratio-based (matching A4 1:√2), capped for viewport
  // Mobile: 90% viewport width, capped at 420
  const maxAvailableHeight = window.innerHeight - 120;
  const mobileWidth = Math.min(window.innerWidth * 0.90, 420);
  const desktopHeight = Math.min(800, maxAvailableHeight);
  const flipbookWidth = isMobile ? mobileWidth : Math.round(desktopHeight / 1.414);
  const flipbookHeight = isMobile ? Math.round(mobileWidth * 1.414) : desktopHeight;

  return (
    <div className="w-full h-screen bg-[#0f172a] flex flex-col relative">
      {/* Top Bar */}
      <div className="flex items-center justify-between px-4 md:px-8 py-4 z-20">
        <Link
          to={getCatalogUrl({
            companyName: product.companyName,
            name: product.name,
          })}
          className="flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm cursor-pointer"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-left-line" />
          </div>
          Xem chi tiết sản phẩm
        </Link>

        <div className="flex items-center gap-2 text-white/50">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-left-s-line text-sm" />
          </div>
          <span className="text-xs hidden sm:inline">Sách điện tử - Vuốt để xem</span>
          <span className="text-[10px] sm:hidden">Vuốt để xem</span>
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-right-s-line text-sm" />
          </div>
        </div>
      </div>

      {/* Flipbook */}
      <div className="flex-1 flex flex-col items-center justify-center overflow-hidden">
        {/* "Sách điện tử" label — mobile only, centered above flipbook */}
        {isMobile && (
          <p className="text-white/40 text-[10px] font-medium tracking-widest uppercase mb-1.5 text-center">
            Sách điện tử
          </p>
        )}

        {/* Flipbook container — relative for absolute arrow positioning on mobile */}
        <div className={`flex items-center justify-center ${isMobile ? 'relative w-full px-1' : 'w-full px-2'}`}>
          {/* Prev Button — absolute on mobile, inline flex on desktop */}
          <button
            onClick={handleFlipPrev}
            disabled={currentPage === 0}
            className={`z-10 flex-shrink-0 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed
              ${isMobile
                ? 'absolute left-1 w-7 h-7'
                : 'w-10 h-10 mr-3'
              }`}
            aria-label="Trang trước"
          >
            <div className={`flex items-center justify-center ${isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'}`}>
              <i className={`ri-arrow-left-s-line ${isMobile ? 'text-lg' : 'text-xl'}`} />
            </div>
          </button>

          <HTMLFlipBook
            ref={flipbookRef}
            width={flipbookWidth}
            height={flipbookHeight}
            size="fixed"
            minWidth={isMobile ? 260 : 280}
            maxWidth={isMobile ? 440 : 800}
            minHeight={isMobile ? 368 : 400}
            maxHeight={isMobile ? 622 : 1100}
            maxShadowOpacity={isMobile ? 0.15 : 0.5}
            showCover={true}
            mobileScrollSupport={false}
            flippingTime={isMobile ? 350 : 800}
            usePortrait={true}
            drawShadow={!isMobile}
            className="flipbook"
            onFlip={(e) => setCurrentPage(e.data)}
            clickEventForward={false}
            useMouseEvents={true}
            swipeDistance={isMobile ? 20 : 30}
            disableFlipByClick={false}
          >
            {pages.map((page, i) => (
              <div key={i} className="page">
                {page}
              </div>
            ))}
          </HTMLFlipBook>

          {/* Next Button — absolute on mobile, inline flex on desktop */}
          <button
            onClick={handleFlipNext}
            disabled={currentPage >= pages.length - 1}
            className={`z-10 flex-shrink-0 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed
              ${isMobile
                ? 'absolute right-1 w-7 h-7'
                : 'w-10 h-10 ml-3'
              }`}
            aria-label="Trang sau"
          >
            <div className={`flex items-center justify-center ${isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'}`}>
              <i className={`ri-arrow-right-s-line ${isMobile ? 'text-lg' : 'text-xl'}`} />
            </div>
          </button>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="flex items-center justify-between px-4 md:px-8 py-3 z-20">
        <div />
        <span className="text-white/40 text-sm">
          Trang {currentPage + 1} / {pages.length}
        </span>
        <button
          onClick={handleCopyLink}
          className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white/70 hover:text-white text-xs px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
        >
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i
              className={
                copied ? "ri-check-line text-green-400" : "ri-links-line text-xs"
              }
            />
          </div>
          {copied ? "Đã sao chép!" : "Sao chép link"}
        </button>
      </div>
    </div>
  );
}